// ═══════════════════════════════════════════════════════════════════════════
//  useBreakpoint — ONE resize listener for the whole console.
//  Mobile blueprint Phase 2 (God 2026-09-04).
//
//  Before this, thirteen components each ran their own
//  `useState(window.innerWidth < 768)` plus their own resize listener, each
//  with its own threshold. That is thirteen listeners firing on every resize
//  frame, thirteen independent re-render cascades, and — the part that
//  actually bit — thirteen places where "mobile" could mean a different width.
//  A card could be in mobile layout while the panel beside it was not.
//
//  This module keeps ONE listener and one value, and every consumer reads the
//  same snapshot through useSyncExternalStore, so there is no tearing: all
//  components in a single render pass agree on the breakpoint by construction.
//
//  Thresholds are deliberately named, not numeric, at the call site:
//     phone   < 640    one column, bottom nav, tables collapse to cards
//     tablet  < 1024   two columns, sidebar overlays
//     desktop >= 1024  the full console
//
//  768 was the old ad-hoc number. 640 is the phone boundary because an iPhone
//  Pro Max is 430 CSS px and an iPad mini portrait is 744 — 768 put the iPad
//  into phone layout and wasted half its screen.
// ═══════════════════════════════════════════════════════════════════════════
import { useSyncExternalStore } from 'react';

export type Breakpoint = 'phone' | 'tablet' | 'desktop';

// `legacy` is 768 on purpose and must not be "cleaned up" to 640 or 1024.
// Thirteen components already branch on `width < 768`. Migrating them onto this
// store has to be behaviour-preserving: if the number moves, every one of those
// call sites silently changes what the DESKTOP console does between 768 and
// 1023 px, which is a real window size God uses. New phone work uses `isPhone`
// (<640); `isMobile` keeps meaning exactly what it meant before.
export const BP = { phone: 640, legacy: 768, tablet: 1024 } as const;

export interface Viewport {
  width: number;
  height: number;
  bp: Breakpoint;
  /** `isMobile` keeps its LEGACY meaning (width < 768) so migrating the
   *  thirteen existing call sites changes no behaviour at all. Use `isPhone`
   *  (< 640) for new phone-specific layout. */
  isPhone: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isPortrait: boolean;
  /** A PHONE turned sideways (touch screen, landscape, < 500 px tall). An iPhone in
   *  landscape is 844 px wide, so the width test calls it a desktop and the chart
   *  got the desktop chrome — measured 2026-09-24: 219 of 340 px left for the chart.
   *  Chart pages now go full-screen here instead (God 2026-09-24). */
  isLandPhone: boolean;
}

const bpFor = (w: number): Breakpoint =>
  w < BP.phone ? 'phone' : w < BP.tablet ? 'tablet' : 'desktop';

function measure(): Viewport {
  // Guard for SSR / very early calls — never throw into a render.
  const w = typeof window === 'undefined' ? 1440 : window.innerWidth;
  const h = typeof window === 'undefined' ? 900 : window.innerHeight;
  const bp = bpFor(w);
  return {
    width: w, height: h, bp,
    isPhone: bp === 'phone',
    isTablet: bp === 'tablet',
    isMobile: w < BP.legacy,      // LEGACY 768 — see the note on BP above
    isDesktop: bp === 'desktop',
    isPortrait: h >= w,
    isLandPhone: w > h && h < 500 && (() => {
      try { return window.matchMedia('(pointer: coarse)').matches; } catch { return false; }
    })(),
  };
}

// ── the store ───────────────────────────────────────────────────────────────
// A cached snapshot object is REQUIRED, not an optimisation.
// useSyncExternalStore compares snapshots by reference and re-renders whenever
// it gets a new one; returning a fresh object from measure() on every
// getSnapshot call would make every consumer re-render on every commit, which
// is worse than the thirteen listeners this replaces.
let snapshot: Viewport = measure();
const listeners = new Set<() => void>();

function recompute() {
  const next = measure();
  if (next.width === snapshot.width && next.height === snapshot.height) return;
  snapshot = next;
  listeners.forEach(l => l());
}

let bound = false;
let raf = 0;
function bind() {
  if (bound || typeof window === 'undefined') return;
  bound = true;
  // Coalesce to one measurement per frame. iOS fires resize continuously while
  // the URL bar collapses and while the on-screen keyboard animates; measuring
  // per event would recompute layout dozens of times for one gesture.
  const onChange = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => { raf = 0; recompute(); });
  };
  window.addEventListener('resize', onChange, { passive: true });
  window.addEventListener('orientationchange', onChange, { passive: true });
  // visualViewport catches the iOS keyboard, which does NOT always fire resize.
  try { window.visualViewport?.addEventListener('resize', onChange, { passive: true }); } catch { /* older iOS */ }
}

function subscribe(cb: () => void) {
  bind();
  listeners.add(cb);
  // A late subscriber may have missed a resize that happened before it mounted.
  recompute();
  return () => { listeners.delete(cb); };
}

const getSnapshot = () => snapshot;

/** The whole viewport. Prefer the narrow hooks below when you only need a flag. */
export function useViewport(): Viewport {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Drop-in for the old per-component `const [isMobile, setIsMobile] = useState(...)`. */
export function useIsMobile(): boolean {
  return useViewport().isMobile;
}

export function useIsPhone(): boolean {
  return useViewport().isPhone;
}

/** For code that must branch three ways rather than two. */
export function useBreakpoint(): Breakpoint {
  return useViewport().bp;
}
